import Defaults from '~/scripts/constants/defaults';
import LinkStyle from './link-style';
import Node from './node';
import NodeDefinition from './node-definition';
import Relations from '../relations';
import Repre from './repre';
import SchemaComment from './schema-comment';
import SchemaLink from './schema-link';
import SchemaNode from './schema-node';

const DEFAULT_VERSION = '1.2';

const privates = new WeakMap();

/**
 * Schema class for Class Designer
 */
// @ts-ignore
export default class Schema extends Node {
	/** @type {Map<import('@knitiv/api-client-javascript').KNode,Node>} */
	#cache;

	#$injector;

	#nextId;

	#KTranslate;

	#NodeService;

	/** @type {import('./user').UserModel} */
	#User;

	/* @ngInject */
	/**
	 * Instantiates a Class Designer Schema
	 *
	 * @class
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('@knitiv/api-client-javascript').KNode | undefined} [kid]
	 */
	constructor(
		$injector,
		kid,
	) {
		super($injector, kid);
		this.#$injector = $injector;
		this.#nextId = 0;
		this.#KTranslate = $injector.get('KTranslate');
		this.#NodeService = $injector.get('NodeService');
		this.#User = $injector.get('User');
		this.#cache = new Map();

		this.create = !kid;

		/** @type {Node[]} */
		this.dico = [];

		this.isa.objid = 'K_NODE;CLASS_DESIGNER_SCHEMA';
		this.addRepre(new Repre({
			value: $injector.get('$translate').instant('designer.new-schema'),
		}));

		/** @type {SchemaComment[]} */
		this.comments = [];

		/** @type {SchemaNode[]} */
		this.schemaNodes = [];

		/** @type {string} */
		this.id = (kid && kid.split(';')[1]) || '';

		/** @type {SchemaLink[]} */
		this.links = [];

		/** @type {import('./droit').default[]} */
		this.droit = [];

		/** @type {any[]} */
		this.relations = [];

		this.version = DEFAULT_VERSION;

		/** @type {any} */
		this.prot = undefined;
		privates.set(this, this.prot);

		Relations.get($injector)
			.then((/** @type {any} */ r) => this.relations.push(...r))
			.catch((/** @type {any} */ error) => console.error(error));
	}

	/**
	 * Adds a schema comment
	 *
	 * @param {any} schemaComment comment object
	 * @returns {SchemaComment} schemaComment object
	 */
	addSchemaComment(schemaComment) {
		const newComment = new SchemaComment();
		newComment.id = schemaComment.id;
		newComment.comment = schemaComment.comment;
		newComment.x = schemaComment.x;
		newComment.y = schemaComment.y;

		this.comments.push(newComment);

		return newComment;
	}

	/**
	 * Adds a schema link
	 *
	 * @param {any} schemaLink object link
	 * @param {boolean} create
	 * @returns {Promise<SchemaLink>} SchemaLink object
	 */
	async addSchemaLink(schemaLink, create = false) {
		const newLink = new SchemaLink(this.#$injector);

		newLink.create = create;
		newLink.dico_id = schemaLink.dico_id;

		if ('linker' in schemaLink) {
			newLink.linker = schemaLink.linker;
		}

		if ('kid' in schemaLink) {
			newLink.kid = schemaLink.kid;
		}

		newLink.type = /^\d$/.test(schemaLink.type) && this.relations[Number.parseInt(schemaLink.type, 10)]
			? this.relations[Number.parseInt(schemaLink.type, 10)].kid
			: schemaLink.type || Defaults.schema.DEFAULT_RELATION;

		if ('style' in schemaLink) {
			newLink.style = Object.assign(new LinkStyle(), schemaLink.style);
		}
		else {
			newLink.style.updateStyleFromRelation(newLink.type);
		}
		newLink.from = schemaLink.from;
		newLink.to = schemaLink.to;
		newLink.data = schemaLink.data || '';
		newLink.readOnly = 'readOnly' in schemaLink ? schemaLink.readOnly : false;
		this.links.push(newLink);

		return newLink;
	}

	/**
	 * Adds a schema node
	 *
	 * @param {SchemaNode | any} schemaNode Instance of SchemaNode object
	 * @param {any} node Optional node details
	 * @returns {Promise<SchemaNode | undefined>} SchemaNode object
	 */
	async addSchemaNode(schemaNode, node = undefined) {
		if (schemaNode instanceof SchemaNode) {
			schemaNode.show();
			this.schemaNodes.push(schemaNode);
			this.#nextId++;
			return schemaNode;
		}

		if (!schemaNode.existK && node?.kid) {
			const nodeExists = await this.#NodeService.get(node.kid);
			if (nodeExists?.kid === node.kid) {
				schemaNode.existK = true;
			}
		}

		let newSchemaNode;
		if (schemaNode.existK && node && this.dico.some(dicoNode => dicoNode.kid === node.kid)) {
			const dicoNode = this.dico.find(n => n.kid === node.kid);
			if (dicoNode) {
				newSchemaNode = new SchemaNode(dicoNode);
				newSchemaNode.existK = schemaNode.existK;
			}
		}
		else {
			newSchemaNode = new SchemaNode(new Node(this.#$injector, schemaNode.kid, 'K_NODE;SYS_CLASSE'));
		}

		if (newSchemaNode) {
			newSchemaNode.id = 'id' in schemaNode ? schemaNode.id : this.#User.getRef();

			const repres = (schemaNode.repre?.list || schemaNode.repre || schemaNode.node?.repre || node?.repre || []).map(
				(/** @type {any} */ repre) => new Repre(repre),
			);

			if (repres) {
				for (const [kr, repre] of Object.entries(repres)) {
					if (!repre.kid) {
						repre.kid = kr;
					}
					newSchemaNode.node.addRepre(repre);
				}
			}
			else if ('name' in schemaNode && schemaNode.node.name) {
				newSchemaNode.node.repre.push(new Repre({
					kid:   `K_NODE;${this.#User.getRefNr()}`,
					value: schemaNode.node.name,
				}));
			}

			if (schemaNode.create !== undefined) {
				newSchemaNode.node.create = schemaNode.create;
			}
			else if (node?.create !== undefined) {
				newSchemaNode.node.create = node.create;
			}

			newSchemaNode.node.kndef = Object.assign(
				new NodeDefinition('kndef'), schemaNode.kndef || schemaNode.node?.kndef || node?.kndef,
			);
			newSchemaNode.node.kndefext = Object.assign(
				new NodeDefinition('kndefext'), schemaNode.kndefext || schemaNode.node?.kndefext || node?.kndefext,
			);
			if (!newSchemaNode.node.kndef.id) {
				newSchemaNode.node.kndef.id = this.#$injector.get('User').getRef().slice(-12);
				newSchemaNode.node.kndef.fixId();
			}
			if (!newSchemaNode.node.kndefext.id) {
				newSchemaNode.node.kndefext.id = newSchemaNode.node.kndef.id;
				newSchemaNode.node.kndefext.fixId();
			}
			schemaNode.kndef = newSchemaNode.node.kndef;
			newSchemaNode.node.type_kid = schemaNode.type_kid || node?.type_kid || 'K_NODE;STRING';
			newSchemaNode.shape = schemaNode.shape || 'RECTANGLE';
			newSchemaNode.node.objtype = schemaNode.objtype || node?.objtype || 'CLASS';
			newSchemaNode.node.isa.objid = schemaNode.isa?.objid || schemaNode.isa || node?.isa.objid || 'K_NODE;SYS_CLASSE';
			newSchemaNode.x = schemaNode.x;
			newSchemaNode.y = schemaNode.y;

			if (!('existK' in schemaNode) && 'readOnly' in schemaNode) {
				// this is for compatibility purposes because the flag 'existK' was recently added
				newSchemaNode.existK = schemaNode.readOnly;
			}
			else {
				// newer version here
				newSchemaNode.existK = 'existK' in schemaNode ? schemaNode.existK : false;
				newSchemaNode.readOnly = 'readOnly' in schemaNode ? schemaNode.readOnly : false;
			}

			if (!('_visible' in schemaNode)) {
				newSchemaNode.show();
			}

			const droits = schemaNode.droit?.list || schemaNode.droit || node?.droit;
			if (Array.isArray(droits)) {
				for (const droit of droits) {
					newSchemaNode.node.addDroit(droit);
				}
			}

			const droitcusts = schemaNode.droitcust?.list ?? schemaNode.droitcust ?? node?.droitcust;
			if (Array.isArray(droitcusts)) {
				for (const droitcust of droitcusts) {
					newSchemaNode.node.addDroitCust({
						index:    droitcust.index,
						kright:   droitcust.kright,
						order:    droitcust.order,
						org:      droitcust.org || { kid: droitcust.org_kid },
						role:     droitcust.role || { kid: droitcust.role_kid },
						suborder: droitcust.suborder,
					});
				}
			}

			const droitdefs = schemaNode.droitdef?.list ?? schemaNode.droitdef ?? node?.droitdef;
			if (Array.isArray(droitdefs)) {
				for (const droitdef of droitdefs) {
					newSchemaNode.node.addDroitDef({
						index:    droitdef.index,
						kright:   droitdef.kright,
						order:    droitdef.order,
						org:      droitdef.org || { kid: droitdef.org_kid },
						role:     droitdef.role || { kid: droitdef.role_kid },
						suborder: droitdef.suborder,
					});
				}
			}

			this.schemaNodes.push(newSchemaNode);
			this.#nextId++;

			return newSchemaNode;
		}

		return undefined;
	}

	/**
	 * @param {any} data
	 * @returns {import('angular').IPromise<any>}
	 */
	check12(data) {
		const $q = this.#$injector.get('$q');

		/** @type {any[]} */
		const nodesToRebuild = [];

		// check kid existence in common.node
		if (data.common.nodes.some((/** @type {any} */ node) => !('kid' in node))) {
			// check links
			for (const link of data.schema.links) {
				for (const endpoint of ['from', 'to']) {
					const { kid } = link[endpoint];
					if (
						kid
						&& !data.common.nodes.some((/** @type {any} */ node) => node.kid && node.kid === kid)
						&& !nodesToRebuild.some(nodeToRebuild => nodeToRebuild.node.kid === kid)
					) {
						const nodeIndex = data.schema.nodes.findIndex(
							(/** @type {any} */ schemaNode) => schemaNode.id === link[endpoint].id,
						);
						if (nodeIndex >= 0) {
							data.schema.nodes[nodeIndex].kid = kid;
							nodesToRebuild.push({ node: data.schema.nodes[nodeIndex], key: nodeIndex });
						}
					}
				}
				if (
					link.linker && link.linker.kid !== '0'
					&& !data.common.nodes.some((/** @type {any} */ node) => node.kid && node.kid === link.linker.kid)
					&& !nodesToRebuild.some(nodeToRebuild => nodeToRebuild.node.kid === link.linker.kid)
				) {
					const nodeIndex = data.schema.nodes.findIndex(
						(/** @type {any} */ schemaNode) => schemaNode.kid === link.linker.kid,
					);
					if (nodeIndex >= 0) {
						data.schema.nodes[nodeIndex].kid = link.linker.kid;
						nodesToRebuild.push({ node: data.schema.nodes[nodeIndex], key: nodeIndex });
					}
				}
			}
		}

		// check shema nodes without kid
		for (const [key, schemaNode] of data.schema.nodes.entries()) {
			if (!schemaNode.kid) {
				nodesToRebuild.push({ node: { ...schemaNode }, key });
			}
		}

		const promises = nodesToRebuild.map(({ node, key }) => {
			const deferred = $q.defer();
			if (node.existK && node.kid) {
				// eslint-disable-next-line promise/no-nesting
				this.#NodeService.load(node.kid).then((/** @type {any} */ knode) => {
					data.common.nodes.push(knode);
					return deferred.resolve(knode);
				}).catch(() => {});
			}
			else if (node.shape === 'RECTANGLE') {
				const knode = this.#NodeService.create('K_NODE;SYS_CLASSE', node.kid);
				data.schema.nodes[key].kid = knode.kid;
				data.common.nodes.push(knode);
				deferred.resolve(knode);
			}
			else {
				console.warn('Unknown type node', node);
				deferred.resolve(false);
			}

			return deferred.promise;
		});

		return $q.all(promises).then(() => data);
	}

	/**
	 * Export the schema
	 *
	 * @param {string} version Version
	 * @returns {Object} Object representation of the schema to export
	 */
	export(version) {
		switch (version || this.version) {
		case '1.2': {
			return this.export12();
		}

		default: {
			return this.export11();
		}
		}
	}

	export11() {
		return {
			droit:     this.droit.map(droit => droit.export11()),
			info:      {
				name: this.getName(),
			},
			links:     this.links.map(schemaLink => schemaLink.export11()),
			nodes:     this.schemaNodes.filter(n => n.isVisible()).map(schemaNode => schemaNode.export11()),
			version:   '1.1',
		};
	}

	export12() {
		/** @type {any} */
		const output = {
			info:    {
				name: this.getName(),
			},
			version: '1.2',
			schema: {
				comments: this.comments.map(comment => comment.export()),
				droit: this.droit.map(droit => droit.export()),
				droitcust: this.droitcust.map(droitcust => droitcust.export()),
				droitdef: this.droitdef.map(droitdef => droitdef.export()),
				links: this.links.map(schemaLink => schemaLink.export()),
				nodes: [],
			},
			common: {
				nodes: [],
			},
		};

		for (const /** @type {SchemaNode} */ schemaNode of this.schemaNodes) {
			if (schemaNode.isVisible()) {
				output.schema.nodes.push(schemaNode.export());

				if (!output.common.nodes.some((/** @type {Node} */ n) => n.kid === schemaNode.node.kid)) {
					output.common.nodes.push({
						attribut:  schemaNode.node.attribut,
						create:    schemaNode.node.create,
						droit:     schemaNode.node.droit.map(droit => droit.export()),
						droitcust: schemaNode.node.droitcust.map(droitcust => droitcust.export()),
						droitdef:  schemaNode.node.droitdef.map(droitdef => droitdef.export()),
						isa:       schemaNode.node.isa,
						kid:       schemaNode.node.kid,
						kjson:     schemaNode.node.kjson,
						kndef:     schemaNode.node.kndef,
						objtype:   schemaNode.node.objtype,
						repre:     schemaNode.node.repre,
						type_kid:  schemaNode.node.type_kid,
					});
				}
			}
		}

		return output;
	}

	async fixSchemaLinks() {
		for (const schemaNode of this.schemaNodes) {
			if (Array.isArray(schemaNode.node.kndef.CLASS.COMMON)) {
				for (const common of schemaNode.node.kndef.CLASS.COMMON) {
					if (common.dico_id in schemaNode.node.kndef.CLASS.DICO) {
						const dico = schemaNode.node.kndef.CLASS.DICO[common.dico_id];
						const schemaLink = this.links.find(l => l.dico_id === common.dico_id);
						const schemaNodeTo = this.schemaNodes.find(
							n => schemaLink && ((n.id && n.id === schemaLink.to?.id)
								|| (!schemaLink.to && n.node.kid === dico.objnum2_kid)),
						);

						if (schemaLink && schemaNodeTo) {
							/** @type {{kid: import('@knitiv/api-client-javascript').LinkerKNode, name: string | undefined}} */
							const linker = {
								kid: '0',
								name: undefined,
							};

							if (dico.linker_kid !== '0') {
								linker.kid = dico.linker_kid;
								const linkerNode = this.schemaNodes.find(n => n.node.kid === dico.linker_kid);
								linker.name = linkerNode
									? linkerNode.getName() : (await this.#KTranslate.request(null, dico.linker_kid));
							}

							schemaLink.linker = linker;
							schemaLink.from = {
								id:   schemaNode.id,
								kid:  schemaNode.node.kid,
								card: common.card_from,
							};
							schemaLink.to = {
								id:   schemaNodeTo.id,
								kid:  schemaNodeTo.node.kid,
								card: common.card_to,
							};
							schemaLink.data = common.data;
							schemaLink.type = dico.relation_kid;
						}
					}
				}
			}
		}
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} kid
	 * @returns {string | undefined}
	 */
	getNodeIdFromKid(kid) {
		const n = this.schemaNodes.find(schemaNode => schemaNode.node.kid === kid && schemaNode.isVisible());
		return n?.id;
	}

	/**
	 * Get node by id
	 *
	 * @param {string} id Node id
	 * @returns {SchemaNode | undefined} SchemaNode object if found, else undefined
	 */
	getNode(id) {
		// noinspection JSValidateTypes
		return this.schemaNodes.find(schemaNode => schemaNode.id === id && schemaNode.isVisible());
	}

	/**
	 * @param {any} data
	 * @param {any} dico
	 * @returns {Promise<Schema>}
	 */
	async load(data, dico) {
		this.version = data.schema.version;

		// temp fix: fetch missing repre using ObjectInfo
		if (!this.create) {
			/** @type {any} */
			const info = await this.#NodeService.load(this.kid);

			if (info) {
				// @ts-ignore
				this.dico.push(...Object.values(dico).map(
					node => this.#NodeService.loadRes(node, null, true, true, false),
				));
				this.repre = info.repre;
			}
		}
		// end temp fix

		const name = this.getName();
		if (name) {
			this.setName(name);
		}

		switch (this.version) {
		case '1.2': {
			return this.load12(data.schema);
		}

		default: {
			return this.load11(data.schema);
		}
		}
	}

	/**
	 * @param {any} data
	 * @returns {Promise<Schema>}
	 */
	async load11(data) {
		this.droit = data.droit;

		if (Array.isArray(data?.nodes)) {
			for (const schemaNode of data.nodes) {
				await this.addSchemaNode(schemaNode, schemaNode.node);
			}
		}
		if (Array.isArray(data?.links)) {
			for (const schemaLink of data.links) {
				await this.addSchemaLink(schemaLink);
			}
		}
		await this.fixSchemaLinks();
		return this;
	}

	/**
	 * @param {any} data
	 * @returns {Promise<Schema>}
	 */
	async load12(data) {
		if (data.schema?.comments) {
			for (const comment of data.schema.comments) {
				this.addSchemaComment(comment);
			}
		}

		if (data.schema?.droit) {
			for (const [kid, droit] of Object.entries(data.schema.droit)) {
				droit.kid = kid;
				this.addDroit(droit);
			}
		}

		if (data.schema?.droitcust) {
			for (const [kid, droitcust] of Object.entries(data.schema.droitcust)) {
				droitcust.kid = kid;
				this.addDroitCust(droitcust);
			}
		}

		if (data.schema?.droitdef) {
			for (const [kid, droitdef] of Object.entries(data.schema.droitdef)) {
				droitdef.kid = kid;
				this.addDroitDef(droitdef);
			}
		}

		// eslint-disable-next-line no-shadow
		const rawSchema = await this.check12(data);

		if (Array.isArray(rawSchema.schema?.nodes)) {
			for (const schemaNode of rawSchema.schema.nodes) {
				await this.addSchemaNode(
					schemaNode,
					rawSchema.common.nodes.find((/** @type {any} */ node) => node.kid === schemaNode.kid),
				);
			}
		}
		if (Array.isArray(rawSchema.schema?.links)) {
			for (const schemaLink of rawSchema.schema.links) {
				await this.addSchemaLink(schemaLink);
			}
		}
		await this.fixSchemaLinks();

		return this;
	}

	// noinspection JSUnusedGlobalSymbols
	/**
	 * Checks if a node exists in schema by KID
	 *
	 * @param {string} kid Node KID
	 * @returns {boolean} SchemaNode object if found
	 */
	nodeExists(kid) {
		return this.schemaNodes.some(schemaNode => schemaNode.node?.kid === kid && schemaNode.isVisible());
	}

	/**
	 * Remove a link from the schema
	 *
	 * @param {SchemaLink} schemaLink
	 */
	removeSchemaLink(schemaLink) {
		if ('dico_id' in schemaLink && schemaLink.from.id) {
			const source = this.getNode(schemaLink.from.id);
			if (source && schemaLink.dico_id !== undefined) {
				const key = source.node.kndef.CLASS?.COMMON?.findIndex(
					(/** @type {any} */ item) => item.dico_id === schemaLink.dico_id,
				);

				if (key >= 0) {
					source.node.kndef.remove('CLASS', 'COMMON', key);
				}
				if (schemaLink.dico_id !== null) {
					source.node.kndef.remove('CLASS', 'DICO', schemaLink.dico_id);
				}
			}
		}
		this.links.splice(this.links.indexOf(schemaLink), 1);
	}
}
